import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft,
  Download,
  FileText,
  Printer,
  Share2,
  Send,
  CheckCircle,
  XCircle,
  TrendingUp,
  Edit,
  Eye,
  Clock,
  AlertCircle,
  RefreshCw,
  User,
  Package,
  History,
} from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import QuotationPDF from '../../components/office/QuotationPDF';
import { pdf } from '@react-pdf/renderer';

const QuotationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalAction, setApprovalAction] = useState('approve');

  const isAdmin = ['admin', 'super_admin', 'manager'].includes(currentUser?.role);
  const isSales = currentUser?.role === 'sales';

  // Fetch quotation
  const { data: quote, isLoading, error, refetch } = useQuery({
    queryKey: ['quotation', id],
    queryFn: () => pb.collection('quotations').getOne(id, { expand: 'clientJobId' }),
    enabled: !!id,
  });

  // Fetch versions
  const { data: versions = [] } = useQuery({
    queryKey: ['quotation-versions', quote?.quoteNumber?.split('/')[0]],
    queryFn: async () => {
      if (!quote) return [];
      const baseNumber = quote.quoteNumber.split('/')[0];
      return pb.collection('quotations').getFullList({
        filter: `quoteNumber ~ "${baseNumber}"`,
        sort: '-created',
      });
    },
    enabled: !!quote,
  });

  // Mutations
  const updateStatus = useMutation({
    mutationFn: ({ data }) => pb.collection('quotations').update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['quotation', id]);
      queryClient.invalidateQueries(['quotation-versions']);
      queryClient.invalidateQueries(['quotations']);
      toast.success('Status updated');
      setShowApprovalDialog(false);
      refetch();
    },
    onError: (err) => toast.error('Failed: ' + err.message),
  });

  const sendToClient = useMutation({
    mutationFn: () =>
      pb.collection('quotations').update(id, {
        status: 'Sent',
        sentToClient: true,
        sentAt: new Date().toISOString(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['quotation', id]);
      toast.success('Quotation sent to client');
      refetch();
    },
    onError: (err) => toast.error('Failed: ' + err.message),
  });

  const sendForApproval = useMutation({
    mutationFn: async () => {
      const admins = await pb.collection('users').getFullList({
        filter: 'role = "admin" || role = "super_admin" || role = "manager"',
      });
      for (const admin of admins) {
        try {
          await pb.collection('notifications').create({
            userId: admin.id,
            type: 'quotation_approval',
            message: `${currentUser?.name} sent quotation for approval.`,
            quotationId: id,
            isRead: false,
          });
        } catch {}
      }
      return pb.collection('quotations').update(id, {
        status: 'Pending Approval',
        approvalStatus: 'pending',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['quotation', id]);
      toast.success('Sent for approval. Admins notified.');
      refetch();
    },
    onError: (err) => toast.error('Failed: ' + err.message),
  });

  const generateInvoice = useMutation({
    mutationFn: async () => {
      const invoiceData = {
        invoiceNumber: `INV-${quote.quoteNumber}`,
        quotationId: quote.id,
        clientJobId: quote.clientJobId,
        clientName: quote.clientName,
        clientEmail: quote.clientEmail,
        clientPhone: quote.clientPhone,
        clientAddress: quote.clientAddress,
        items: quote.items,
        subtotal: quote.subtotal,
        discountAmount: quote.discountAmount,
        taxAmount: quote.taxAmount,
        total: quote.total,
        status: 'Draft',
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        notes: quote.notes,
        createdBy: currentUser?.id,
      };
      const record = await pb.collection('invoices').create(invoiceData);
      await pb.collection('quotations').update(id, { status: 'Invoiced' });
      return record;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['quotation', id]);
      queryClient.invalidateQueries(['invoices']);
      toast.success('Invoice generated!');
      refetch();
    },
    onError: (err) => toast.error('Failed: ' + err.message),
  });

  const handleApproval = (action) => {
    const data = {
      status: action === 'approve' ? 'Approved' : 'Rejected',
      approvalStatus: action === 'approve' ? 'approved' : 'rejected',
      approvedBy: currentUser?.id,
      approvedAt: new Date().toISOString(),
      notes: approvalNotes || quote.notes,
    };
    updateStatus.mutate({ data });
  };

  // Helper: calculate totals
  const calculateTotals = () => {
    if (!quote) return { subtotal: 0, discount: 0, tax: 0, total: 0 };
    const subtotal = quote.items?.reduce((sum, i) => sum + i.rate * i.quantity, 0) || 0;
    const discount = quote.discountAmount || 0;
    const tax = quote.taxAmount || 0;
    return { subtotal, discountAmount: discount, taxAmount: tax, total: subtotal - discount + tax };
  };

  const totals = calculateTotals();

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price || 0);

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const getStatusBadge = (status) => {
    const config = {
      Draft: { color: 'bg-gray-100 text-gray-700', icon: Clock },
      'Pending Approval': { color: 'bg-yellow-100 text-yellow-700', icon: TrendingUp },
      Approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      Rejected: { color: 'bg-red-100 text-red-700', icon: AlertCircle },
      Sent: { color: 'bg-blue-100 text-blue-700', icon: Send },
      Invoiced: { color: 'bg-purple-100 text-purple-700', icon: FileText },
    };
    const { color, icon: Icon } = config[status] || config.Draft;
    return (
      <Badge className={`${color} flex items-center gap-1 w-fit`}>
        <Icon className="w-3 h-3" /> {status}
      </Badge>
    );
  };


  const handlePrintPDF = async () => {
    if (!quote) return;
    setPrinting(true);
    try {
      const totals = calculateTotals();
      const doc = (
        <QuotationPDF
          quote={quote}
          items={quote.items || []}
          subtotal={totals.subtotal}
          discountAmount={totals.discountAmount}
          taxAmount={totals.taxAmount}
          total={totals.total}
          clientJobIdText={quote.clientJobIdText || quote.expand?.clientJobId?.jobId}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          window.open(url, '_blank');
        }
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        }, 60000);
      };
    } catch (err) {
      console.error('Print failed:', err);
      toast.error('Print failed: ' + err.message);
    } finally {
      setPrinting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="text-center py-16">
        <p className="text-red-500">Quotation not found.</p>
        <Button onClick={() => navigate('/office/quotations')} className="mt-4">
          Back to Quotations
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/office/quotations')}
              className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <FileText className="w-8 h-8" />
                Quotation Details
              </h1>
              <p className="text-white/80">
                #{quote.quoteNumber} • {formatDate(quote.created)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Edit - only if draft */}
            {(isSales || isAdmin) && quote.status === 'Draft' && (
              <Button
                onClick={() => navigate(`/office/quotation/edit/${quote.id}`)}
                variant="outline"
                className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white gap-2"
              >
                <Edit className="w-4 h-4" /> Edit
              </Button>
            )}

            {/* Send for Approval - only sales on draft */}
            {isSales && quote.status === 'Draft' && (
              <Button
                onClick={() => {
                  if (window.confirm('Send this quotation for approval?')) {
                    sendForApproval.mutate();
                  }
                }}
                className="bg-yellow-500 text-white hover:bg-yellow-600 gap-2"
              >
                <TrendingUp className="w-4 h-4" /> Send for Approval
              </Button>
            )}

            {/* Approve / Reject - only admin on pending */}
            {isAdmin && quote.status === 'Pending Approval' && (
              <>
                <Button
                  onClick={() => {
                    setApprovalAction('approve');
                    setShowApprovalDialog(true);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white gap-2"
                >
                  <CheckCircle className="w-4 h-4" /> Approve
                </Button>
                <Button
                  onClick={() => {
                    setApprovalAction('reject');
                    setShowApprovalDialog(true);
                  }}
                  variant="destructive"
                  className="gap-2"
                >
                  <XCircle className="w-4 h-4" /> Reject
                </Button>
              </>
            )}

            {/* Send to Client - after approved */}
            {quote.status === 'Approved' && !quote.sentToClient && (isSales || isAdmin) && (
              <Button
                onClick={() => {
                  if (window.confirm('Send this quotation to the client?')) {
                    sendToClient.mutate();
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              >
                <Send className="w-4 h-4" /> Send to Client
              </Button>
            )}

            {/* Generate Invoice - admin only after approved */}
            {isAdmin && quote.status === 'Approved' && !quote.sentToClient && (
              <Button
                onClick={() => {
                  if (window.confirm('Generate invoice from this quotation?')) {
                    generateInvoice.mutate();
                  }
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
              >
                <FileText className="w-4 h-4" /> Generate Invoice
              </Button>
            )}

            {/* Download PDF */}
            <PDFDownloadLink
              document={
                <QuotationPDF
                  quote={quote}
                  items={quote.items || []}
                  subtotal={totals.subtotal}
                  discountAmount={totals.discountAmount}
                  taxAmount={totals.taxAmount}
                  total={totals.total}
                  clientJobIdText={quote.clientJobIdText || quote.expand?.clientJobId?.jobId}
                />
              }
              fileName={`quotation_${quote.quoteNumber}.pdf`}
            >
              {({ loading }) => (
                <Button
                  variant="outline"
                  disabled={loading}
                  className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white gap-2"
                >
                  <Download className="w-4 h-4" /> {loading ? 'Generating...' : 'PDF'}
                </Button>
              )}
            </PDFDownloadLink>

            <Button
              variant="outline"
              onClick={() => refetch()}
              className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white gap-2"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Status summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 rounded-full">
              <FileText className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Status</p>
              <p className="text-lg font-bold">{getStatusBadge(quote.status)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-50 rounded-full">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Approval</p>
              <p className="text-lg font-bold">
                {quote.approvalStatus === 'pending' && <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>}
                {quote.approvalStatus === 'approved' && <Badge className="bg-green-100 text-green-700">Approved</Badge>}
                {quote.approvalStatus === 'rejected' && <Badge className="bg-red-100 text-red-700">Rejected</Badge>}
                {!quote.approvalStatus && <span className="text-slate-400">—</span>}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-50 rounded-full">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Total Value</p>
              <p className="text-lg font-bold text-green-600">{formatPrice(totals.total)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs - Details and History */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="details" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg">
            Details
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg">
            History ({versions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Quotation Info */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                <CardTitle className="flex items-center gap-2 text-indigo-800">
                  <FileText className="w-5 h-5" /> Quotation Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-3">
                <div className="flex justify-between border-b pb-2">
                  <span className="text-slate-500">Quote Number</span>
                  <span className="font-mono font-semibold">{quote.quoteNumber}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-slate-500">Date Created</span>
                  <span>{formatDate(quote.created)}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-slate-500">Valid Until</span>
                  <span>{quote.validUntil ? formatDate(quote.validUntil) : '—'}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-slate-500">Status</span>
                  {getStatusBadge(quote.status)}
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-slate-500">Approval</span>
                  <div>
                    {quote.approvalStatus === 'pending' && <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>}
                    {quote.approvalStatus === 'approved' && <Badge className="bg-green-100 text-green-700">Approved</Badge>}
                    {quote.approvalStatus === 'rejected' && <Badge className="bg-red-100 text-red-700">Rejected</Badge>}
                    {!quote.approvalStatus && <span className="text-slate-400">—</span>}
                  </div>
                </div>
                {quote.approvedBy && (
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-slate-500">Approved By</span>
                    <span>{quote.expand?.approvedBy?.name || '—'}</span>
                  </div>
                )}
                {quote.approvedAt && (
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-slate-500">Approved At</span>
                    <span>{formatDate(quote.approvedAt)}</span>
                  </div>
                )}
                {quote.sentToClient && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Sent to Client</span>
                    <span className="text-green-600 font-medium">{formatDate(quote.sentAt)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Info */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100">
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <User className="w-5 h-5" /> Client Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-3">
                <div className="flex justify-between border-b pb-2">
                  <span className="text-slate-500">Name</span>
                  <span className="font-medium">{quote.clientName}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-slate-500">Phone</span>
                  <span>{quote.clientPhone}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-slate-500">Email</span>
                  <span>{quote.clientEmail}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-slate-500">Address</span>
                  <span className="text-right">{quote.clientAddress || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Job ID</span>
                  <span className="font-mono">{quote.clientJobId || '—'}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Items Table */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
              <CardTitle className="flex items-center gap-2 text-indigo-800">
                <Package className="w-5 h-5" /> Packages / Items
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="text-indigo-700 font-semibold">Package</TableHead>
                    <TableHead className="text-indigo-700 font-semibold">Description</TableHead>
                    <TableHead className="text-indigo-700 font-semibold">Unit</TableHead>
                    <TableHead className="text-indigo-700 font-semibold text-right">Qty</TableHead>
                    <TableHead className="text-indigo-700 font-semibold text-right">Rate (₹)</TableHead>
                    <TableHead className="text-indigo-700 font-semibold text-right">Amount (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quote.items?.map((item, idx) => (
                    <TableRow key={idx} className="hover:bg-indigo-50/50">
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>{item.description || '—'}</TableCell>
                      <TableCell>{item.unit || '—'}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">{formatPrice(item.rate)}</TableCell>
                      <TableCell className="text-right font-semibold">{formatPrice(item.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {(!quote.items || quote.items.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No items in this quotation
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Totals */}
              <div className="mt-6 flex justify-end">
                <div className="w-80 space-y-2 bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span className="font-medium">{formatPrice(totals.subtotal)}</span>
                  </div>
                  {totals.discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount</span>
                      <span>-{formatPrice(totals.discountAmount)}</span>
                    </div>
                  )}
                  {totals.taxAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>GST ({quote.taxRate || 18}%)</span>
                      <span>+{formatPrice(totals.taxAmount)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between font-bold text-lg">
                      <span>Grand Total</span>
                      <span className="text-indigo-600">{formatPrice(totals.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {quote.notes && (
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <FileText className="w-5 h-5" /> Notes & Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-slate-600 whitespace-pre-wrap">{quote.notes}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card className="border-0 shadow-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
              <CardTitle className="flex items-center gap-2 text-indigo-800">
                <History className="w-5 h-5" /> Version History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="text-indigo-700 font-semibold">Version</TableHead>
                    <TableHead className="text-indigo-700 font-semibold">Date</TableHead>
                    <TableHead className="text-indigo-700 font-semibold">Status</TableHead>
                    <TableHead className="text-indigo-700 font-semibold">Total</TableHead>
                    <TableHead className="text-indigo-700 font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versions.map((v) => (
                    <TableRow
                      key={v.id}
                      className={v.id === quote.id ? 'bg-indigo-50/70 hover:bg-indigo-100/70' : 'hover:bg-indigo-50/50'}
                    >
                      <TableCell className="font-mono">
                        {v.quoteNumber}
                        {v.id === quote.id && (
                          <Badge className="ml-2 bg-indigo-100 text-indigo-700">Current</Badge>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(v.created)}</TableCell>
                      <TableCell>{getStatusBadge(v.status)}</TableCell>
                      <TableCell className="font-semibold">{formatPrice(v.total || 0)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/office/quotation/${v.id}`)}
                            title="View this version"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <PDFDownloadLink
                            document={
                              <QuotationPDF
                                quote={v}
                                items={v.items || []}
                                subtotal={v.subtotal || 0}
                                discountAmount={v.discountAmount || 0}
                                taxAmount={v.taxAmount || 0}
                                total={v.total || 0}
                                clientJobIdText={v.clientJobIdText || v.expand?.clientJobId?.jobId}
                              />
                            }
                            fileName={`quotation_${v.quoteNumber}.pdf`}
                          >
                            {({ loading }) => (
                              <Button variant="ghost" size="sm" disabled={loading} title="Download PDF">
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                          </PDFDownloadLink>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {versions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-slate-500">
                        No version history available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Footer Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={handlePrintPDF} disabled={printing} className="gap-2">
          <Printer className="w-4 h-4" /> {printing ? 'Preparing…' : 'Print'}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
            toast.success('Link copied to clipboard');
          }}
          className="gap-2"
        >
          <Share2 className="w-4 h-4" /> Share Link
        </Button>
      </div>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{approvalAction === 'approve' ? 'Approve Quotation' : 'Reject Quotation'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Quotation: <span className="font-semibold">{quote.quoteNumber}</span> for{' '}
              <span className="font-semibold">{quote.clientName}</span>
            </p>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                rows={3}
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder={approvalAction === 'approve' ? 'Add approval notes...' : 'Reason for rejection...'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleApproval(approvalAction)}
              className={approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {approvalAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotationDetail;